import './menu.scss';

import { ITabProps, routes } from '../../../routing';
import {
  NavLink,
  generatePath,
  matchPath,
  useLocation
} from 'react-router-dom';
import React, { useCallback, useState, useMemo } from 'react';
import { SCREEN_WIDTH } from '../../../consts';
import appRoutes from '../../../routing/routes/apps';
import classNames from 'classnames';
import { getWindowDimensions } from '../../utils/CommonFunction';
import kebabCase from 'lodash/kebabCase';
import strings from '../../../strings';
import styles from '../index.module.scss';
import { isEmpty } from 'lodash';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { useResizeScreen } from '../../../hooks/useResizeScreen';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface IMenuProps {
  onClickMenuItem: () => void;
}

type ToggleMenuFn = (
  tab: ITabProps,
  isAppsMenu: boolean,
  e: React.MouseEvent<Element, MouseEvent>
) => void;

function MenuParentNavLink({
  tab,
  isAppsMenu,
  path,
  hasChildren,
  navLinkActive,
  subMenuOpened,
  toggleMenu
}: {
  tab: ITabProps;
  isAppsMenu: boolean;
  path: string;
  hasChildren: boolean;
  navLinkActive: boolean;
  subMenuOpened: boolean;
  toggleMenu: ToggleMenuFn;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      toggleMenu(tab, isAppsMenu, e);
    },
    [tab, isAppsMenu, toggleMenu]
  );

  return (
    <NavLink
      className={classNames('nav-link-item nav-parent-item', {
        active: navLinkActive
      })}
      activeClassName="active"
      exact={tab.exact === undefined ? !hasChildren : tab.exact}
      onClick={handleClick}
      to={{ pathname: path }}>
      <div className="slide-icon">{tab.icon}</div>
      <span>{tab.title}</span>
      {tab.child && tab.child.length > 0 && (
        <FontAwesomeIcon
          className={classNames('arrow-icon', {
            active: subMenuOpened
          })}
          icon={faAngleRight as IconProp}
        />
      )}
    </NavLink>
  );
}

function MenuChildNavLink({
  navChild,
  isAppsMenu,
  childPath,
  handleClickSubMenuItem
}: {
  navChild: ITabProps;
  isAppsMenu: boolean;
  childPath: string;
  handleClickSubMenuItem: ToggleMenuFn;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      handleClickSubMenuItem(navChild, isAppsMenu, e);
    },
    [navChild, isAppsMenu, handleClickSubMenuItem]
  );

  return (
    <NavLink
      className="nav-link-item nav-link-item-sub"
      activeClassName="active"
      onClick={handleClick}
      exact={navChild.exact === undefined ? true : navChild.exact}
      to={{ pathname: childPath }}>
      <div className="nav-link-item-sub-title">{navChild.title}</div>
    </NavLink>
  );
}

export default function Menu({ onClickMenuItem }: IMenuProps) {
  const location = useLocation();
  const [tabsWithChild, setTabsWithChild] = useState<string[]>([]);

  const route = routes.find((r) => matchPath(location.pathname, r)) || null;
  const match = route ? matchPath(location.pathname, route) : null;
  const tabs = useMemo(() => route?.tabs || [], [route?.tabs]);

  const handleClickOutside = () => {
    if (!isEmpty(tabsWithChild) && isTabletMode()) {
      setTabsWithChild([]);
    }
  };

  const windowResize = () => {
    if (isTabletMode()) {
      setTabsWithChild([]);
    }
  };

  const ref = useOutsideClick(handleClickOutside);

  useResizeScreen(windowResize);

  // Checking menu is opening
  const isOpenMenu = (id: string) => {
    return tabsWithChild.includes(id);
  };

  // The logic for opening and closing this menu, which contains several submenus
  const toggleMenu = useCallback(
    (
      tab: ITabProps,
      isAppsMenu: boolean,
      e: React.MouseEvent<Element, MouseEvent>
    ) => {
      const id = kebabCase(tab.title);
      if (!tab.child) {
        onClickMenuItem();
        isTabletMode() && setTabsWithChild([]);
      } else {
        e.preventDefault();
        let newTabsWithChild = [];
        if (tabsWithChild.includes(id)) {
          newTabsWithChild = tabsWithChild.filter(
            (tabItem: string) => tabItem !== id
          );
        } else {
          if (isTabletMode()) {
            newTabsWithChild = [id];
          } else {
            newTabsWithChild = [...tabsWithChild, id];
          }
        }
        setTabsWithChild(newTabsWithChild);
      }
      handleClickAppsMenu(tab, isAppsMenu, e);
    },
    [onClickMenuItem, tabsWithChild]
  );

  const handleClickAppsMenu = (
    tab: ITabProps,
    isAppsMenu: boolean,
    e: React.MouseEvent<Element, MouseEvent>
  ) => {
    if (isAppsMenu) {
      if (!tab.child) {
        e.preventDefault();
        window.open(tab.url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const isTabletMode = () => {
    const { width } = getWindowDimensions();
    return width > SCREEN_WIDTH.MOBILE && width <= SCREEN_WIDTH.TABLET;
  };

  // Checking the external link for Apps menu
  const handleClickSubMenuItem: ToggleMenuFn = useCallback(
    (tab, isAppsMenu, e) => {
      if (isTabletMode()) {
        // ipad mode will hide sub menu after clicked
        setTabsWithChild([]);
      }
      onClickMenuItem();
      handleClickAppsMenu(tab, isAppsMenu, e);
    },
    [onClickMenuItem]
  );

  // Set active for parent menu
  const isActiveParentTab = (path: string, child?: ITabProps[]) => {
    if (!isEmpty(child)) {
      return !isEmpty(
        child?.find((navChild: ITabProps) => navChild.path === match?.path)
      );
    }
    return match?.url === path;
  };

  function renderTab(tab: ITabProps, isAppsMenu = false) {
    const id = kebabCase(tab.title);
    const path = match ? generatePath(tab.path, match.params) : '/';
    const hasChildren = (tab.child?.length || 0) > 0;
    const navLinkActive =
      isActiveParentTab(tab.path, tab.child) ||
      (isOpenMenu(id) && isTabletMode());

    return (
      <li key={id} className="nav-item">
        <MenuParentNavLink
          tab={tab}
          isAppsMenu={isAppsMenu}
          path={path}
          hasChildren={hasChildren}
          navLinkActive={navLinkActive}
          subMenuOpened={isOpenMenu(id)}
          toggleMenu={toggleMenu}
        />
        {hasChildren && (
          <ul
            className={classNames(
              'nav-item-sub collapse',
              {
                opened: isOpenMenu(id)
              },
              {
                active: isActiveParentTab(tab.path, tab.child)
              }
            )}>
            <li className="nav-item-sub-menu">
              <NavLink
                className={classNames('nav-link-item-sub-menu', {
                  active:
                    isActiveParentTab(tab.path, tab.child) || isOpenMenu(id)
                })}
                activeClassName="active"
                exact={tab.exact === undefined ? true : tab.exact}
                to={{ pathname: path }}>
                <div className="nav-link-item-sub-title">{tab.title}</div>
              </NavLink>
              <div className="fill-nav-item"></div>
            </li>
            {tab.child?.map((navChild: ITabProps) => {
              const childId = kebabCase(navChild.title);
              const childPath = match
                ? generatePath(navChild.path, match.params)
                : '/';

              return (
                <li key={childId}>
                  <MenuChildNavLink
                    navChild={navChild}
                    isAppsMenu={isAppsMenu}
                    childPath={childPath}
                    handleClickSubMenuItem={handleClickSubMenuItem}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  }

  return (
    <div
      className={classNames(
        'side-nav pt-3 has-scrollbar',
        {
          'sub-menu-opening': tabsWithChild.length > 0
        },
        styles.containerNav
      )}
      ref={ref}>
      <li className="side-nav-title">{strings.leftMenuNavigation}</li>
      {tabs.map((item) => renderTab(item, false))}
      <div className="apps-navigation">
        <li className="side-nav-title">{strings.leftMenuApps}</li>
        {appRoutes.map((item) => renderTab(item, true))}
      </div>
    </div>
  );
}
